export interface FieldData {
  label: string;
  value: string;
}

export interface LeftContentItem {
  type: string;
  title: string;
  content: string;
}

export interface PageData {
  leftContent: LeftContentItem[];
  propertyFields: FieldData[];
  hasExtraInfo: boolean;
}

export interface HeightPaginationOptions {
  isLandscape: boolean;
}

// Estimate row height for pagination (approximate values)
export const estimateFieldHeight = (value: string): number => {
  if (!value || value === "—") return 32; // Base row height in pixels

  // Rough calculation based on content length and wrapping
  const baseHeight = 32;
  const charWidth = 6; // Average character width in pixels at text-xs
  const cellWidth = 200; // Approximate half table width
  const charsPerLine = Math.floor(cellWidth / charWidth);
  const lines = Math.max(1, Math.ceil(value.length / charsPerLine));

  // Add extra height for line breaks in content
  const lineBreaks = (value.match(/\n/g) || []).length;
  const totalLines = lines + lineBreaks;

  return baseHeight + (totalLines - 1) * 16; // 16px per additional line
};

// Split a field value at natural break points to fit remaining page height
export const splitFieldForPage = (
  label: string,
  value: string,
  remainingHeight: number,
) => {
  if (!value || value === "—")
    return { currentPagePart: { label, value }, nextPagePart: null };

  const estimatedHeight = estimateFieldHeight(value);

  // If field fits in remaining height, don't split
  if (estimatedHeight <= remainingHeight) {
    return { currentPagePart: { label, value }, nextPagePart: null };
  }

  // Calculate how much content can fit in remaining height
  const baseHeight = 32;
  const availableLines = Math.max(
    1,
    Math.floor((remainingHeight - baseHeight) / 16) + 1,
  );
  const charsPerLine = Math.floor(200 / 6); // Same calculation as estimateFieldHeight
  const maxCharsForPage = availableLines * charsPerLine;

  // Don't split if it would create a very small split
  if (maxCharsForPage < value.length * 0.2) {
    return { currentPagePart: null, nextPagePart: { label, value } };
  }

  // Find best split point within the character limit
  let splitIndex = Math.min(maxCharsForPage, value.length);

  // Try to split at natural break points
  const paragraphBreak = value.lastIndexOf("\n\n", splitIndex);
  const sentenceBreak = value.lastIndexOf(". ", splitIndex);
  const wordBreak = value.lastIndexOf(" ", splitIndex);

  if (paragraphBreak > splitIndex * 0.4) {
    splitIndex = paragraphBreak + 2;
  } else if (sentenceBreak > splitIndex * 0.4) {
    splitIndex = sentenceBreak + 2;
  } else if (wordBreak > splitIndex * 0.4) {
    splitIndex = wordBreak + 1;
  }

  const currentPart = value.substring(0, splitIndex).trim();
  const nextPart = value.substring(splitIndex).trim();

  return {
    currentPagePart: currentPart ? { label, value: currentPart } : null,
    nextPagePart: nextPart
      ? { label: `${label} (continued)`, value: nextPart }
      : null,
  };
};

// Create pages based on actual content height with intelligent field splitting
export const createHeightBasedPages = (
  propertyFields: FieldData[],
  options: HeightPaginationOptions,
): FieldData[][] => {
  const { isLandscape } = options;
  const pages: FieldData[][] = [];
  let currentPropertyFields: FieldData[] = [];
  let currentPageHeight = 0;
  let fieldIndex = 0;
  let pageNumber = 0; // Track which page we're creating

  // Available height for content - first page has less space due to header
  const getMaxPageHeight = (isFirstPage: boolean) => {
    const baseHeight = isLandscape ? 700 : 900;
    // First page has header that takes ~100-120px, so reduce available height
    return isFirstPage ? baseHeight - 120 : baseHeight;
  };

  while (fieldIndex < propertyFields.length) {
    const field = propertyFields[fieldIndex];
    const fieldHeight = estimateFieldHeight(field.value);
    const maxPageHeight = getMaxPageHeight(pageNumber === 0);
    const remainingHeight = maxPageHeight - currentPageHeight;

    // If field fits completely, add it
    if (fieldHeight <= remainingHeight) {
      currentPropertyFields.push(field);
      currentPageHeight += fieldHeight;
      fieldIndex++;
    }
    // If field is too big, try to split it
    else {
      const { currentPagePart, nextPagePart } = splitFieldForPage(
        field.label,
        field.value,
        remainingHeight,
      );

      // Add current page part if it exists
      if (currentPagePart) {
        currentPropertyFields.push(currentPagePart);
      }

      // Start new page
      if (currentPropertyFields.length > 0) {
        pages.push([...currentPropertyFields]);
        currentPropertyFields = [];
        currentPageHeight = 0;
        pageNumber++; // Increment page number when starting new page
      }

      // Handle next page part
      if (nextPagePart) {
        // Replace the original field with the remaining part for next iteration
        propertyFields[fieldIndex] = {
          label: nextPagePart.label,
          value: nextPagePart.value,
        };
        // Don't increment fieldIndex - process the remaining part
      } else {
        // No remaining part, move to next field
        fieldIndex++;
      }
    }

    // If we've reached max page capacity, start new page
    if (
      currentPageHeight >= getMaxPageHeight(pageNumber === 0) &&
      currentPropertyFields.length > 0
    ) {
      pages.push([...currentPropertyFields]);
      currentPropertyFields = [];
      currentPageHeight = 0;
      pageNumber++; // Increment page number when starting new page
    }
  }

  // Add remaining fields to last page
  if (currentPropertyFields.length > 0) {
    pages.push(currentPropertyFields);
  }

  return pages;
};
